/**
 * Wizard chrome: the desktop blue brand pane (logo + step rail + serif quote)
 * and the mobile top brand bar + progress fill. Both render the accent gradient
 * from CSS vars (no raw hex per brand.md — the gradient is built from
 * --accent → --accent-hover). The mobile progress fill animates via transform:
 * scaleX (transform-only motion rule), not a width transition.
 */
import { OnboardingLogo } from "./OnboardingLogo";
import { StepRail } from "./StepRail";

const GRADIENT = "linear-gradient(165deg, var(--accent), var(--accent-hover))";

function Wordmark() {
  return <span className="font-display text-[19px] font-semibold text-white">A2 Easy</span>;
}

export function OnboardingBrandPane({
  step,
  labels,
  quote,
  onJump,
}: {
  step: number;
  labels: string[];
  quote: string;
  onJump: (i: number) => void;
}) {
  return (
    <aside
      className="relative hidden w-[340px] flex-none flex-col overflow-hidden px-8 py-9 lg:flex"
      style={{ background: GRADIENT }}
    >
      {/* Decorative blurred circles — warm bottom-right, white top-left */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-[70px] -bottom-[50px] h-60 w-60 rounded-full"
        style={{ background: "var(--warm)", opacity: 0.35 }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -left-[50px] top-[60px] h-36 w-36 rounded-full bg-white/[0.06]"
      />
      <div className="relative z-[1] flex items-center gap-2.5">
        <OnboardingLogo />
        <Wordmark />
      </div>
      <StepRail step={step} labels={labels} onJump={onJump} />
      <p className="relative z-[1] mt-auto font-serif-italic text-[23px] leading-[1.35] text-white/90">
        {quote}
      </p>
    </aside>
  );
}

export function OnboardingProgress({ step, total }: { step: number; total: number }) {
  const fill = (step + 1) / total;
  return (
    <div className="lg:hidden">
      <div className="flex items-center gap-2.5 px-5 py-4" style={{ background: GRADIENT }}>
        <OnboardingLogo />
        <Wordmark />
      </div>
      <div className="h-[5px] bg-[var(--panel-soft)]" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={total}>
        <div
          className="h-full origin-left rounded-r-[3px] bg-[var(--warm)] transition-transform duration-300 ease-out"
          style={{ transform: `scaleX(${fill})`, width: "100%" }}
        />
      </div>
    </div>
  );
}
